import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead as apiMarkAllRead,
  markRead as apiMarkRead,
  type AppNotification,
} from "@a/notifications";
import { supabase } from "@cl/supabase";
import { useAuth } from "@h/useCtx";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";

const LIST_KEY = ["notifications", "list"] as const;
const COUNT_KEY = ["notifications", "count"] as const;

// Module-level refcounted realtime channel. useNotifications is called by both
// NotificationBell and NotificationPanel; a per-mount subscription meant two
// live channels + double invalidations whenever the panel was open. One shared
// channel per user, torn down when the last consumer unmounts.
let sharedChannel: ReturnType<typeof supabase.channel> | null = null;
let sharedChannelUserId: string | null = null;
let subscriberCount = 0;

function acquireNotificationsChannel(
  userId: string,
  onInsert: () => void,
): () => void {
  if (sharedChannel && sharedChannelUserId !== userId) {
    supabase.removeChannel(sharedChannel);
    sharedChannel = null;
    subscriberCount = 0;
  }
  if (!sharedChannel) {
    sharedChannelUserId = userId;
    sharedChannel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        onInsert,
      )
      .subscribe();
  }
  subscriberCount += 1;
  return () => {
    subscriberCount -= 1;
    if (subscriberCount <= 0 && sharedChannel) {
      supabase.removeChannel(sharedChannel);
      sharedChannel = null;
      sharedChannelUserId = null;
      subscriberCount = 0;
    }
  };
}

export function useNotifications() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => fetchNotifications(30),
    staleTime: 60_000,
    enabled: !!user,
  });

  const count = useQuery({
    queryKey: COUNT_KEY,
    queryFn: fetchUnreadCount,
    staleTime: 60_000,
    enabled: !!user,
  });

  // Realtime: bump on any insert for this user. Shared channel (refcounted) so
  // Bell + Panel don't open two subscriptions.
  useEffect(() => {
    if (!user) return;
    return acquireNotificationsChannel(user.id, () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: COUNT_KEY });
    });
  }, [user, qc]);

  const markReadMut = useMutation({
    mutationFn: apiMarkRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: COUNT_KEY });
    },
  });

  const markAllMut = useMutation({
    mutationFn: apiMarkAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: COUNT_KEY });
    },
  });

  return {
    notifications: (list.data ?? []) as AppNotification[],
    unreadCount: count.data ?? 0,
    isLoading: list.isLoading,
    markRead: markReadMut.mutate,
    markAllRead: markAllMut.mutate,
  };
}
